import { useMatches } from "@tanstack/react-router";
import { useEffect } from "react";

interface RouteTitleMatch {
  readonly meta?: unknown;
}

function resolveRouteDocumentTitle(
  matches: readonly RouteTitleMatch[],
): string | null {
  for (let matchIndex = matches.length - 1; matchIndex >= 0; matchIndex -= 1) {
    const meta = matches[matchIndex]?.meta;
    if (!Array.isArray(meta)) {
      continue;
    }

    for (let metaIndex = meta.length - 1; metaIndex >= 0; metaIndex -= 1) {
      const descriptor: unknown = meta[metaIndex];
      if (
        typeof descriptor === "object" &&
        descriptor !== null &&
        "title" in descriptor &&
        typeof descriptor.title === "string"
      ) {
        return descriptor.title;
      }
    }
  }

  return null;
}

/**
 * Sole route-driven writer of the *visible* document title.
 *
 * The static SPA shell (index.html, rewritten per-route at the edge for
 * crawlers) ships a `<title>` that stays the first title element in `<head>`.
 * `HeadContent` is portaled into `document.head`, so its React-managed
 * `<title>` lands after the static one — and browsers display only the first
 * title element. Route-head titles are therefore invisible unless something
 * assigns `document.title`, which writes that first element. This component
 * is that something: it mirrors the deepest active route's meta title into
 * `document.title` on navigation.
 *
 * The passive effect is load-bearing: React flushes unmount cleanups before
 * mount/update effects in the same pass, so this write lands after an
 * unmounting page restores its captured title (see the symbol page's
 * `useLiveQuoteDocumentTitle`) and after head reconciliation. In-page
 * imperative writers such as the live quote stay visible because this effect
 * re-fires only when the *route* title changes.
 */
export function RouterDocumentTitleSync() {
  const title = useMatches({
    select: resolveRouteDocumentTitle,
  });

  useEffect(() => {
    if (title !== null) {
      document.title = title;
    }
  }, [title]);

  return null;
}
